import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { FaPlus, FaEdit, FaTrash, FaSearch } from 'react-icons/fa';
import './AdminManagement.css';
import { API_ENDPOINTS } from '../../config/api';

const PublicationTypeManagement = () => {
  const [publicationTypes, setPublicationTypes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  
  // Debug modal state
  useEffect(() => {
    console.log('Publication Type Management Modal state changed:', showModal);
  }, [showModal]);
  
  const [editingType, setEditingType] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [error, setError] = useState('');

  const [formData, setFormData] = useState({
    name: '',
    description: ''
  });

  useEffect(() => {
    fetchPublicationTypes();
  }, []);

  const fetchPublicationTypes = async () => {
    try {
      const response = await axios.get(API_ENDPOINTS.PUBLICATION_TYPES);
      setPublicationTypes(response.data);
    } catch (err) {
      setError('Failed to fetch publication types');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    try {
      if (editingType) {
        await axios.put(`${API_ENDPOINTS.PUBLICATION_TYPES}/${editingType.id}`, formData);
      } else {
        await axios.post(API_ENDPOINTS.PUBLICATION_TYPES, formData);
      }
      
      fetchPublicationTypes();
      handleCloseModal();
    } catch (err) {
      setError(err.response?.data?.error || 'Operation failed');
    }
  };

  const handleDelete = async (typeId) => {
    if (window.confirm('Are you sure you want to delete this publication type?')) {
      try {
        await axios.delete(`${API_ENDPOINTS.PUBLICATION_TYPES}/${typeId}`);
        fetchPublicationTypes();
      } catch (err) {
        setError(err.response?.data?.error || 'Delete failed');
      }
    }
  };

  const handleEdit = (type) => {
    setEditingType(type);
    setFormData({
      name: type.name,
      description: type.description || ''
    });
    setShowModal(true);
  };

  const handleAdd = () => {
    setEditingType(null);
    setFormData({
      name: '',
      description: ''
    });
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setEditingType(null);
    setError('');
  };

  const filteredTypes = publicationTypes.filter(type =>
    type.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    type.description?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  if (loading) {
    return <div className="loading">Loading...</div>;
  }

  return (
    <div className="admin-management">
      <div className="page-header">
        <h2>Publication Type Management</h2>
        <button className="add-button" onClick={handleAdd}>
          <FaPlus /> Add Publication Type
        </button>
      </div>

      <div className="search-bar">
        <div className="search-input">
          <FaSearch />
          <input
            type="text"
            placeholder="Search publication types..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>
      </div>

      {error && <div className="error-message">{error}</div>}

      <div className="table-container">
        <table className="data-table">
          <thead>
            <tr>
              <th>Name</th>
              <th>Description</th>
              <th>Created</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {filteredTypes.map(type => (
              <tr key={type.id}>
                <td>{type.name}</td>
                <td>{type.description || 'No description'}</td>
                <td>{new Date(type.created_at).toLocaleDateString()}</td>
                <td>
                  <div className="action-buttons">
                    <button
                      className="action-btn edit"
                      onClick={() => handleEdit(type)}
                      title="Edit"
                    >
                      <FaEdit />
                    </button>
                    <button
                      className="action-btn delete"
                      onClick={() => handleDelete(type.id)}
                      title="Delete"
                    >
                      <FaTrash />
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Modal */}
      {showModal && (
        <div 
          onClick={handleCloseModal}
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(0, 0, 0, 0.6)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 9999,
            padding: '20px'
          }}
        >
          <div 
            onClick={(e) => e.stopPropagation()}
            style={{
              background: 'white',
              borderRadius: '12px',
              width: '100%',
              maxWidth: '500px',
              maxHeight: '90vh',
              overflowY: 'auto',
              boxShadow: '0 20px 40px rgba(0, 0, 0, 0.3)',
              position: 'relative',
              zIndex: 10000
            }}
          >
            <div className="modal-header">
              <h3>{editingType ? 'Edit Publication Type' : 'Add New Publication Type'}</h3>
              <button className="close-button" onClick={handleCloseModal}>×</button>
            </div>
            
                          <form onSubmit={handleSubmit} className="modal-form">
                <div className="form-group">
                  <label className="required">Publication Type Name</label>
                  <input
                    type="text"
                    value={formData.name}
                    onChange={(e) => setFormData({...formData, name: e.target.value})}
                    required
                    placeholder="Enter publication type name"
                  />
                </div>

              <div className="form-group">
                <label>Description</label>
                <textarea
                  value={formData.description}
                  onChange={(e) => setFormData({...formData, description: e.target.value})}
                  placeholder="Enter publication type description"
                  rows="4"
                />
              </div>

              <div className="modal-actions">
                <button type="button" onClick={handleCloseModal} className="cancel-button">
                  Cancel
                </button>
                <button type="submit" className="save-button">
                  {editingType ? 'Update' : 'Create'} Publication Type
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default PublicationTypeManagement; 